import React from 'react';
import { Table, Tooltip, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { PvpSyncApp } from '@/types/pvp';
import { BY_APP_DESYNC_TIP, appName, fmtNum } from './model';

const num = (
  title: string,
  render: (a: PvpSyncApp) => string
): ColumnsType<PvpSyncApp>[number] => ({
  title,
  key: title,
  align: 'right',
  render: (_, a) => render(a),
});

const columns: ColumnsType<PvpSyncApp> = [
  {
    title: 'App',
    key: 'app',
    render: (_, a) => {
      const app = appName(a.app);
      // A phone that sent no version is shown in italics, apart from a real app called "unknown".
      return (
        app ?? (
          <Typography.Text type="secondary" italic>
            unknown
          </Typography.Text>
        )
      );
    },
  },
  { title: 'Build', key: 'bv', render: (_, a) => (a.bv == null ? '—' : a.bv) },
  { title: 'Platform', key: 'platform', render: (_, a) => a.platform || '—' },
  num('Matches', (a) => fmtNum(a.matches, 0)),
  num('Balls', (a) => fmtNum(a.balls, 0)),
  {
    ...num('Desync / 1,000 phone-balls', (a) => fmtNum(a.desyncPer1000)),
    title: (
      <Tooltip title={BY_APP_DESYNC_TIP}>
        <span>Desync / 1,000 phone-balls</span>
      </Tooltip>
    ),
  },
  num('Start error p95 (ms)', (a) => fmtNum(a.startErrP95)),
  num('Slack p95 (ms)', (a) => fmtNum(a.slackP95)),
  num('Gap max p95 (ms)', (a) => fmtNum(a.gapMaxP95)),
];

/** The ten busiest (app, build, platform) groups; a match counts for each group of its two phones. */
export const ByAppTable: React.FC<{ rows: PvpSyncApp[] }> = ({ rows }) => (
  <Table<PvpSyncApp>
    size="small"
    pagination={false}
    rowKey={(a) =>
      `${appName(a.app) === null ? 'null' : `app:${a.app}`}/${a.bv ?? ''}/${a.platform ?? ''}`
    }
    columns={columns}
    dataSource={rows}
    scroll={{ x: true }}
  />
);
